import ExcelJS from 'exceljs';
import {
  isCompNode,
  isMechanicalItemNode,
  type AnyNode,
  type CompNode,
  type Sheet,
  type WireEdge,
} from './store';
import { computeNets } from './nets';
import type { ProjectMeta } from './types';

/**
 * Canonical wire-direction normalisation. The user shouldn't have to think
 * about which way a wire was drawn — alphabetically earlier component is
 * always presented as "from", so a connector with N outgoing wires reads
 * as N entries on the same side regardless of drag direction.
 */
function canonEndpoints(
  aComp: string | undefined,
  aConn: string | undefined,
  aPin: string | undefined,
  bComp: string | undefined,
  bConn: string | undefined,
  bPin: string | undefined,
): {
  fromComp: string;
  fromConn: string;
  fromPin: string;
  toComp: string;
  toConn: string;
  toPin: string;
} {
  const ac = (aComp ?? '?').toLowerCase();
  const bc = (bComp ?? '?').toLowerCase();
  const acn = (aConn ?? '').toLowerCase();
  const bcn = (bConn ?? '').toLowerCase();
  const apn = (aPin ?? '?').toLowerCase();
  const bpn = (bPin ?? '?').toLowerCase();
  let swap = false;
  if (ac !== bc) swap = ac > bc;
  else if (acn !== bcn) swap = acn > bcn;
  else swap = apn > bpn;
  return swap
    ? {
        fromComp: bComp ?? '?',
        fromConn: bConn ?? '',
        fromPin: bPin ?? '?',
        toComp: aComp ?? '?',
        toConn: aConn ?? '',
        toPin: aPin ?? '?',
      }
    : {
        fromComp: aComp ?? '?',
        fromConn: aConn ?? '',
        fromPin: aPin ?? '?',
        toComp: bComp ?? '?',
        toConn: bConn ?? '',
        toPin: bPin ?? '?',
      };
}

async function saveWorkbook(wb: ExcelJS.Workbook, filename: string) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeader(row: ExcelJS.Row) {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1E40AF' },
  };
  row.alignment = { vertical: 'middle', horizontal: 'center' };
  row.height = 22;
  row.eachCell((cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF0F172A' } },
      left: { style: 'thin', color: { argb: 'FF0F172A' } },
      bottom: { style: 'thin', color: { argb: 'FF0F172A' } },
      right: { style: 'thin', color: { argb: 'FF0F172A' } },
    };
  });
}

function styleDataRow(row: ExcelJS.Row, even: boolean) {
  row.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
  row.eachCell((cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    };
    if (even) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF8FAFC' },
      };
    }
  });
}

/** Netlist as a proper .xlsx — CSV was a locale minefield (Turkish Excel
 *  expects `;` separators and guesses the codepage), xlsx sidesteps both. */
export async function exportNetlistXlsx(rows: string[][], filename: string) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Netlist', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = [
    { header: 'Net', key: 'net', width: 24 },
    { header: 'Component', key: 'component', width: 40 },
    { header: 'Pin', key: 'pin', width: 16 },
    { header: 'Sheet', key: 'sheet', width: 30 },
  ];
  styleHeader(ws.getRow(1));
  rows.forEach((r, i) => {
    styleDataRow(ws.addRow(r), i % 2 === 0);
  });
  ws.autoFilter = { from: 'A1', to: 'D1' };
  await saveWorkbook(wb, filename);
}

export async function exportWiringXlsx(
  meta: ProjectMeta,
  allNodes: AnyNode[],
  edges: WireEdge[],
  filename: string,
  allSheets?: Sheet[],
) {
  const nodes: CompNode[] = allNodes.filter(isCompNode);
  const wb = new ExcelJS.Workbook();
  wb.creator = meta.author || 'Skematiq';
  wb.created = new Date();

  // Build a quick id → bundle lookup for resolving harness names. Bundles
  // live on every interconnect sheet, so we flatten them all.
  const allBundles = (allSheets ?? [])
    .filter((sh) => sh.kind === 'interconnect')
    .flatMap((sh) => sh.bundles ?? []);
  const bundleById = new Map(allBundles.map((b) => [b.id, b]));
  const harnessName = (id: string | undefined) =>
    id ? bundleById.get(id)?.name ?? '' : '';

  const ws = wb.addWorksheet('Wiring', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = [
    { header: '#', key: 'idx', width: 5 },
    { header: 'Wire No', key: 'wireNo', width: 10 },
    { header: 'Harness', key: 'harness', width: 18 },
    { header: 'From Component', key: 'fromComp', width: 28 },
    { header: 'From Connector', key: 'fromConn', width: 20 },
    { header: 'From Pin', key: 'fromPin', width: 16 },
    { header: 'To Component', key: 'toComp', width: 28 },
    { header: 'To Connector', key: 'toConn', width: 20 },
    { header: 'To Pin', key: 'toPin', width: 16 },
    { header: 'Net', key: 'netName', width: 16 },
    { header: 'Label', key: 'label', width: 18 },
    { header: 'Gauge (AWG)', key: 'gauge', width: 12 },
    { header: 'Color', key: 'color', width: 14 },
    { header: 'Color 2', key: 'color2', width: 12 },
    { header: 'Notes', key: 'notes', width: 32 },
  ];

  styleHeader(ws.getRow(1));

  edges.forEach((e, i) => {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    const srcPin = src?.data.pins.find((p) => p.id === e.sourceHandle);
    const tgtPin = tgt?.data.pins.find((p) => p.id === e.targetHandle);
    const ends = canonEndpoints(
      src?.data.label,
      srcPin?.group,
      srcPin?.name,
      tgt?.data.label,
      tgtPin?.group,
      tgtPin?.name,
    );
    ws.addRow({
      idx: i + 1,
      wireNo: e.data?.wireNo ?? '',
      harness: harnessName(e.data?.harnessId),
      fromComp: ends.fromComp,
      fromConn: ends.fromConn,
      fromPin: ends.fromPin,
      toComp: ends.toComp,
      toConn: ends.toConn,
      toPin: ends.toPin,
      netName: (e.data?.netName ?? '').trim() || (e.data?.label ?? ''),
      label: e.data?.label ?? '',
      gauge: e.data?.gauge ?? '',
      color: e.data?.color ?? '',
      color2: e.data?.color2 ?? '',
      notes: e.data?.notes ?? '',
    });
  });

  for (let r = 2; r <= ws.rowCount; r += 1) {
    styleDataRow(ws.getRow(r), r % 2 === 0);
  }

  ws.autoFilter = { from: 'A1', to: 'O1' };

  // ----- Per-harness production sheets -----------------------------------
  // For every harness that has at least one wire assigned ANYWHERE in the
  // project, emit a dedicated worksheet listing those wires. This is the
  // sheet a harness assembler reads to build the cable: name, gauge, colour,
  // both endpoints, both pin numbers, length, conduit, etc.
  // Schematic-only — the single source of truth, matching the interconnect UI.
  // (Block-diagram edges are the abstract view and carry no pins.)
  const wiresInProject = (allSheets ?? [])
    .filter((sh) => sh.kind === 'schematic')
    .flatMap((sh) => sh.edges.map((e) => ({ edge: e, sheetName: sh.name })));
  const allComponentNodes = (allSheets ?? [])
    .filter((sh) => sh.kind === 'schematic')
    .flatMap((sh) => sh.nodes.filter(isCompNode));

  const usedSheetNames = new Set<string>();
  for (const bundle of allBundles) {
    const wires = wiresInProject.filter(
      ({ edge }) => edge.data?.harnessId === bundle.id,
    );
    if (wires.length === 0) continue;

    // Excel sheet names cap at 31 chars and disallow some characters. Dedup so
    // same-named (or same-prefix) harnesses don't collide and abort the export.
    const base = `Harness_${bundle.name || bundle.id}`.replace(/[\\/?*[\]:]/g, '_').slice(0, 31);
    let safeName = base;
    let n = 2;
    while (usedSheetNames.has(safeName.toLowerCase())) {
      const suffix = `_${n}`;
      safeName = base.slice(0, 31 - suffix.length) + suffix;
      n += 1;
    }
    usedSheetNames.add(safeName.toLowerCase());
    const hs = wb.addWorksheet(safeName, {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    hs.columns = [
      { header: '#', key: 'idx', width: 5 },
      { header: 'Wire No', key: 'wireNo', width: 10 },
      { header: 'From Component', key: 'fromComp', width: 26 },
      { header: 'From Connector', key: 'fromConn', width: 18 },
      { header: 'From Pin', key: 'fromPin', width: 14 },
      { header: 'To Component', key: 'toComp', width: 26 },
      { header: 'To Connector', key: 'toConn', width: 18 },
      { header: 'To Pin', key: 'toPin', width: 14 },
      { header: 'Net', key: 'netName', width: 14 },
      { header: 'Gauge (AWG)', key: 'gauge', width: 12 },
      { header: 'Color', key: 'color', width: 12 },
      { header: 'Color 2', key: 'color2', width: 10 },
      { header: 'Notes', key: 'notes', width: 26 },
    ];
    styleHeader(hs.getRow(1));

    // Compute branched endpoints (DB25 trunk → relay/eth/motor etc.) so the
    // assembler sees the topology, not just a flat wire list.
    const epMap = new Map<
      string,
      { componentId?: string; component: string; connector: string; pins: Set<string> }
    >();
    for (const { edge } of wires) {
      const src = allComponentNodes.find((n) => n.id === edge.source);
      const tgt = allComponentNodes.find((n) => n.id === edge.target);
      const srcPin = src?.data.pins.find((p) => p.id === edge.sourceHandle);
      const tgtPin = tgt?.data.pins.find((p) => p.id === edge.targetHandle);
      const record = (
        componentId: string | undefined,
        component: string | undefined,
        connector: string | undefined,
        pin: string | undefined,
      ) => {
        if (!component) return;
        const key = `${component}||${connector ?? ''}`;
        const existing = epMap.get(key);
        if (existing) {
          if (pin) existing.pins.add(pin);
        } else {
          epMap.set(key, {
            componentId,
            component,
            connector: connector ?? '',
            pins: new Set(pin ? [pin] : []),
          });
        }
      };
      record(src?.id, src?.data.label, srcPin?.group, srcPin?.name);
      record(tgt?.id, tgt?.data.label, tgtPin?.group, tgtPin?.name);
    }
    const harnessEndpoints = Array.from(epMap.values()).sort(
      (a, b) => b.pins.size - a.pins.size,
    );

    // Banner block with bundle metadata + branched endpoint summary.
    hs.insertRow(1, []);
    hs.insertRow(1, [
      `Harness: ${bundle.name}`,
      bundle.fromUnit && bundle.toUnit ? `${bundle.fromUnit} → ${bundle.toUnit}` : '',
      bundle.conduit ? `Conduit: ${bundle.conduit}` : '',
      bundle.length ? `Length: ${bundle.length}` : '',
    ]);
    hs.getRow(1).font = { bold: true, size: 13, color: { argb: 'FF1E40AF' } };
    hs.mergeCells('A1:M1');

    // Endpoints summary line — assembler-friendly "DB25 (12) ┬ DB9 (4) ┬ ..."
    if (harnessEndpoints.length > 0) {
      hs.insertRow(2, [
        'Endpoints:',
        harnessEndpoints
          .map(
            (ep) =>
              `${ep.component}${ep.connector ? ' / ' + ep.connector : ''} (${ep.pins.size} wire${ep.pins.size === 1 ? '' : 's'})`,
          )
          .join('  ·  '),
      ]);
      hs.getRow(2).font = { italic: true, size: 11, color: { argb: 'FF475569' } };
      hs.mergeCells('B2:M2');
    }

    wires.forEach(({ edge }, i) => {
      const src = allComponentNodes.find((n) => n.id === edge.source);
      const tgt = allComponentNodes.find((n) => n.id === edge.target);
      const srcPin = src?.data.pins.find((p) => p.id === edge.sourceHandle);
      const tgtPin = tgt?.data.pins.find((p) => p.id === edge.targetHandle);
      const ends = canonEndpoints(
        src?.data.label,
        srcPin?.group,
        srcPin?.name,
        tgt?.data.label,
        tgtPin?.group,
        tgtPin?.name,
      );
      hs.addRow({
        idx: i + 1,
        wireNo: edge.data?.wireNo ?? '',
        fromComp: ends.fromComp,
        fromConn: ends.fromConn,
        fromPin: ends.fromPin,
        toComp: ends.toComp,
        toConn: ends.toConn,
        toPin: ends.toPin,
        netName: (edge.data?.netName ?? '').trim() || (edge.data?.label ?? ''),
        gauge: edge.data?.gauge ?? '',
        color: edge.data?.color ?? '',
        color2: edge.data?.color2 ?? '',
        notes: edge.data?.notes ?? '',
      });
    });

    // Row layout after the inserts:
    //   1: banner ("Harness: ...")
    //   2: endpoints summary (when present)
    //   3: blank
    //   4: column header
    //   5+: wire data
    const dataStart = harnessEndpoints.length > 0 ? 5 : 4;
    for (let r = dataStart; r <= hs.rowCount; r += 1) {
      styleDataRow(hs.getRow(r), r % 2 === 0);
    }

    // ----- Bill of materials block (connectors + terminations, splices,
    // coverings and wire groups) — derived from the formboard graph. --------
    const graph = bundle.graph;
    const bomRows: Array<[string, string, string, number]> = [];

    // Prefer the stable component-node id (survives renames); the label key
    // is only registered for legacy nodes with no id, so a stale/renamed
    // node can't shadow a live connector that happens to share its old label.
    const termByRef = new Map<string, string>();
    for (const node of graph?.nodes ?? []) {
      if (node.type === 'connector' && node.termination) {
        if (node.refNodeId) termByRef.set(`id:${node.refNodeId}||${node.refConnector ?? ''}`, node.termination);
        else if (node.refComponent) termByRef.set(`lb:${node.refComponent}||${node.refConnector ?? ''}`, node.termination);
      }
    }
    // Live sidecar node ids — only these contribute contact-PN counts below.
    const liveConnectorIds = new Set(
      (graph?.nodes ?? [])
        .filter(
          (node) =>
            node.type === 'connector' &&
            harnessEndpoints.some((ep) =>
              node.refNodeId
                ? node.refNodeId === ep.componentId && (node.refConnector ?? '') === ep.connector
                : node.refComponent === ep.component && (node.refConnector ?? '') === ep.connector,
            ),
        )
        .map((node) => node.id),
    );
    for (const ep of harnessEndpoints) {
      const term =
        (ep.componentId && termByRef.get(`id:${ep.componentId}||${ep.connector}`)) ??
        termByRef.get(`lb:${ep.component}||${ep.connector}`);
      bomRows.push([
        'Connector',
        ep.connector || ep.component,
        `${ep.component} · ${ep.pins.size} pin${ep.pins.size === 1 ? '' : 's'}${term ? ' · ' + term : ''}`,
        1,
      ]);
    }
    for (const node of (graph?.nodes ?? []).filter((n) => n.type === 'junction')) {
      bomRows.push(['Splice', node.label || 'Junction', 'branch / splice point', 1]);
    }
    const coverCount = new Map<string, number>();
    for (const seg of graph?.segments ?? []) {
      if (seg.covering) coverCount.set(seg.covering, (coverCount.get(seg.covering) ?? 0) + 1);
    }
    for (const [name, qty] of coverCount) {
      bomRows.push(['Covering', name, `${qty} segment${qty === 1 ? '' : 's'}`, qty]);
    }
    const wireGroups = new Map<string, { gauge: string; color: string; qty: number }>();
    for (const { edge } of wires) {
      const key = `${edge.data?.gauge ?? ''}||${edge.data?.color ?? ''}`;
      const grp = wireGroups.get(key);
      if (grp) grp.qty += 1;
      else wireGroups.set(key, { gauge: edge.data?.gauge ?? '', color: edge.data?.color ?? '', qty: 1 });
    }
    for (const grp of wireGroups.values()) {
      bomRows.push(['Wire', [grp.gauge, grp.color].filter(Boolean).join(' ') || 'Wire', 'conductor', grp.qty]);
    }
    const contactCount = new Map<string, number>();
    for (const node of graph?.nodes ?? []) {
      if (node.type !== 'connector' || !node.contactPNs || !liveConnectorIds.has(node.id)) continue;
      for (const pn of Object.values(node.contactPNs)) {
        if (pn) contactCount.set(pn, (contactCount.get(pn) ?? 0) + 1);
      }
    }
    for (const [pn, qty] of contactCount) {
      bomRows.push(['Contact', pn, 'crimp contact', qty]);
    }

    if (bomRows.length > 0) {
      hs.addRow([]);
      const titleRow = hs.addRow(['Bill of materials']);
      hs.mergeCells(`A${titleRow.number}:M${titleRow.number}`);
      titleRow.font = { bold: true, size: 12, color: { argb: 'FF1E40AF' } };
      const headerRow = hs.addRow(['#', 'Type', 'Description', 'Detail', 'Qty']);
      styleHeader(headerRow);
      bomRows.forEach((r, i) => {
        styleDataRow(hs.addRow([i + 1, r[0], r[1], r[2], r[3]]), i % 2 === 0);
      });
    }

    // ----- Cut list (segments with a numeric mm length) + total -----------
    const cutSegs = (graph?.segments ?? []).filter((s) => (s.lengthMm ?? 0) > 0);
    if (cutSegs.length > 0) {
      const nodeLabel = (id: string) =>
        graph?.nodes.find((n) => n.id === id)?.label || id.slice(0, 4);
      hs.addRow([]);
      const t = hs.addRow(['Cut list']);
      hs.mergeCells(`A${t.number}:M${t.number}`);
      t.font = { bold: true, size: 12, color: { argb: 'FF1E40AF' } };
      styleHeader(hs.addRow(['#', 'From', 'To', 'Covering', 'Length (mm)']));
      let total = 0;
      cutSegs.forEach((s, i) => {
        total += s.lengthMm ?? 0;
        styleDataRow(
          hs.addRow([i + 1, nodeLabel(s.from), nodeLabel(s.to), s.covering ?? '', s.lengthMm ?? 0]),
          i % 2 === 0,
        );
      });
      const totalRow = hs.addRow(['', '', '', 'TOTAL', total]);
      totalRow.font = { bold: true };
    }

    // ----- Title block / production metadata ------------------------------
    const meta2 = (
      [
        ['Drawing number', bundle.drawingNumber ?? ''],
        ['Revision', bundle.revision ?? ''],
        ['Revision date', bundle.revisionDate ?? ''],
        ['Drawn by', bundle.drawnBy ?? ''],
        ['Checked by', bundle.checkedBy ?? ''],
        ['Approved by', bundle.approvedBy ?? ''],
        ['QA by', bundle.qaBy ?? ''],
        ['Production notes', bundle.productionNotes ?? ''],
      ] as Array<[string, string]>
    ).filter(([, v]) => v);
    if (meta2.length > 0) {
      hs.addRow([]);
      const t = hs.addRow(['Title block']);
      hs.mergeCells(`A${t.number}:M${t.number}`);
      t.font = { bold: true, size: 12, color: { argb: 'FF1E40AF' } };
      for (const [k, v] of meta2) {
        const row = hs.addRow([k, v]);
        row.getCell(1).font = { bold: true };
      }
    }
  }

  await saveWorkbook(wb, filename);
}

export async function exportSpecXlsx(
  meta: ProjectMeta,
  allNodes: AnyNode[],
  edges: WireEdge[],
  filename: string,
  allSheets?: Sheet[],
) {
  const nodes: CompNode[] = allNodes.filter(isCompNode);
  const wb = new ExcelJS.Workbook();
  wb.creator = meta.author || 'Skematiq';
  wb.created = new Date();

  // Summary sheet
  const summary = wb.addWorksheet('Summary');
  summary.columns = [
    { key: 'k', width: 22 },
    { key: 'v', width: 80 },
  ];
  summary.addRow(['Project name', meta.name]);
  summary.addRow(['Author', meta.author]);
  summary.addRow(['Created', meta.createdAt.split('T')[0]]);
  summary.addRow(['Updated', meta.updatedAt.split('T')[0]]);
  summary.addRow(['Component count', nodes.length]);
  summary.addRow(['Wire count', edges.length]);
  summary.addRow(['Other nodes (notes, symbols, splices…)', allNodes.length - nodes.length]);
  summary.addRow(['Description', meta.description]);
  summary.getColumn('k').font = { bold: true };
  summary.getRow(8).alignment = { wrapText: true, vertical: 'top' };

  // Components sheet
  const comps = wb.addWorksheet('Components', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  comps.columns = [
    { header: 'Label', key: 'label', width: 28 },
    { header: 'Category', key: 'category', width: 16 },
    { header: 'Part Number', key: 'partNumber', width: 18 },
    { header: 'Manufacturer', key: 'manufacturer', width: 20 },
    { header: 'Pin Count', key: 'pinCount', width: 10 },
    { header: 'Properties', key: 'props', width: 60 },
  ];
  styleHeader(comps.getRow(1));
  nodes.forEach((n) => {
    const props = Object.entries(n.data.properties)
      .map(([k, v]) => `${k}: ${v}`)
      .join('; ');
    comps.addRow({
      label: n.data.label,
      category: n.data.category,
      partNumber: n.data.partNumber ?? '',
      manufacturer: n.data.manufacturer ?? '',
      pinCount: n.data.pins.length,
      props,
    });
  });
  for (let r = 2; r <= comps.rowCount; r += 1) {
    styleDataRow(comps.getRow(r), r % 2 === 0);
  }
  comps.autoFilter = { from: 'A1', to: 'F1' };

  // Interconnect bundles sheet — gather from all 'interconnect' sheets.
  const allBundles = (allSheets ?? [])
    .filter((sh) => sh.kind === 'interconnect')
    .flatMap((sh) => sh.bundles ?? []);
  if (allBundles.length > 0) {
    const ws = wb.addWorksheet('Harnesses', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    ws.columns = [
      { header: 'Bundle', key: 'name', width: 24 },
      { header: 'Drawing No', key: 'drawingNumber', width: 14 },
      { header: 'Rev', key: 'revision', width: 6 },
      { header: 'From', key: 'fromUnit', width: 18 },
      { header: 'To', key: 'toUnit', width: 18 },
      { header: 'End A conn.', key: 'endAConnector', width: 18 },
      { header: 'End B conn.', key: 'endBConnector', width: 18 },
      { header: 'End A term.', key: 'endATermination', width: 16 },
      { header: 'End B term.', key: 'endBTermination', width: 16 },
      { header: 'Conduit', key: 'conduit', width: 26 },
      { header: 'Length', key: 'length', width: 12 },
      { header: 'Entry (from)', key: 'entryFrom', width: 24 },
      { header: 'Entry (to)', key: 'entryTo', width: 24 },
      { header: 'Cables', key: 'cables', width: 44 },
      { header: 'Notes', key: 'notes', width: 30 },
    ];
    styleHeader(ws.getRow(1));
    allBundles.forEach((b) => {
      ws.addRow({
        name: b.name,
        drawingNumber: b.drawingNumber ?? '',
        revision: b.revision ?? '',
        fromUnit: b.fromUnit,
        toUnit: b.toUnit,
        endAConnector: b.endAConnector ?? '',
        endBConnector: b.endBConnector ?? '',
        endATermination: b.endATermination ?? '',
        endBTermination: b.endBTermination ?? '',
        conduit: b.conduit,
        length: b.length,
        entryFrom: b.entryFrom,
        entryTo: b.entryTo,
        cables: b.cables,
        notes: b.notes,
      });
    });
    for (let r = 2; r <= ws.rowCount; r += 1) {
      styleDataRow(ws.getRow(r), r % 2 === 0);
    }
    ws.getColumn('cables').alignment = { wrapText: true, vertical: 'top' };
    ws.getColumn('notes').alignment = { wrapText: true, vertical: 'top' };
    ws.autoFilter = { from: 'A1', to: 'O1' };
  }

  // Requirements sheet — collect from all 'requirements' sheets in the project.
  const allReqs = (allSheets ?? [])
    .filter((sh) => sh.kind === 'requirements')
    .flatMap((sh) => sh.requirements ?? []);
  if (allReqs.length > 0) {
    const reqs = wb.addWorksheet('Requirements', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    reqs.columns = [
      { header: '#', key: 'num', width: 10 },
      { header: 'Category', key: 'category', width: 14 },
      { header: 'Priority', key: 'priority', width: 10 },
      { header: 'Status', key: 'status', width: 12 },
      { header: 'Title', key: 'title', width: 40 },
      { header: 'Description', key: 'description', width: 55 },
      { header: 'Verification', key: 'verification', width: 40 },
      { header: 'Notes', key: 'notes', width: 40 },
    ];
    styleHeader(reqs.getRow(1));
    allReqs.forEach((r) => {
      reqs.addRow({
        num: r.reqNumber,
        category: r.category,
        priority: r.priority,
        status: r.status,
        title: r.title,
        description: r.description,
        verification: r.verification ?? '',
        notes: r.notes ?? '',
      });
    });
    for (let r = 2; r <= reqs.rowCount; r += 1) {
      styleDataRow(reqs.getRow(r), r % 2 === 0);
    }
    reqs.getColumn('description').alignment = { wrapText: true, vertical: 'top' };
    reqs.getColumn('verification').alignment = { wrapText: true, vertical: 'top' };
    reqs.getColumn('notes').alignment = { wrapText: true, vertical: 'top' };
    reqs.autoFilter = { from: 'A1', to: 'H1' };
  }

  // Mechanical sheet — footprints from every mechanical-layout sheet with
  // real-world dimensions, mass, mounting, orientation and clearance.
  const mechItems = (allSheets ?? [])
    .filter((sh) => sh.kind === 'mechanical')
    .flatMap((sh) => sh.nodes.filter(isMechanicalItemNode).map((n) => ({ n, sheet: sh.name })));
  if (mechItems.length > 0) {
    const mws = wb.addWorksheet('Mechanical', { views: [{ state: 'frozen', ySplit: 1 }] });
    mws.columns = [
      { header: 'Label', key: 'label', width: 28 },
      { header: 'Part Number', key: 'partNumber', width: 18 },
      { header: 'Manufacturer', key: 'manufacturer', width: 18 },
      { header: 'Width (mm)', key: 'w', width: 12 },
      { header: 'Height (mm)', key: 'h', width: 12 },
      { header: 'Depth (mm)', key: 'd', width: 12 },
      { header: 'Mass (kg)', key: 'mass', width: 10 },
      { header: 'Mounting', key: 'mounting', width: 14 },
      { header: 'Mount pose', key: 'pose', width: 12 },
      { header: 'Clearance (mm)', key: 'clr', width: 14 },
      { header: 'Sheet', key: 'sheet', width: 22 },
    ];
    styleHeader(mws.getRow(1));
    for (const { n, sheet } of mechItems) {
      const d = n.data;
      mws.addRow({
        label: d.label,
        partNumber: d.partNumber ?? '',
        manufacturer: d.manufacturer ?? '',
        w: d.widthMm ?? '',
        h: d.heightMm ?? '',
        d: d.depthMm ?? '',
        mass: d.massKg ?? '',
        mounting: d.mounting ?? '',
        pose: d.orientation ?? 'top',
        clr: d.clearanceMm ?? '',
        sheet,
      });
    }
    for (let r = 2; r <= mws.rowCount; r += 1) styleDataRow(mws.getRow(r), r % 2 === 0);
    mws.autoFilter = { from: 'A1', to: 'K1' };
  }

  // BOM sheet — aggregated by (part, manufacturer, label).
  const bom = wb.addWorksheet('BOM', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  bom.columns = [
    { header: 'Qty', key: 'qty', width: 6 },
    { header: 'Label', key: 'label', width: 28 },
    { header: 'Category', key: 'category', width: 16 },
    { header: 'Part Number', key: 'partNumber', width: 18 },
    { header: 'Manufacturer', key: 'manufacturer', width: 20 },
    { header: 'Specs', key: 'specs', width: 60 },
  ];
  styleHeader(bom.getRow(1));
  type BomRow = {
    qty: number;
    label: string;
    category: string;
    partNumber: string;
    manufacturer: string;
    specs: string;
  };
  const bomMap = new Map<string, BomRow>();
  nodes.forEach((n) => {
    const key = [
      (n.data.partNumber ?? '').trim().toLowerCase(),
      (n.data.manufacturer ?? '').trim().toLowerCase(),
      n.data.label.trim().toLowerCase(),
    ].join('::');
    const existing = bomMap.get(key);
    if (existing) {
      existing.qty += 1;
    } else {
      const specs = Object.entries(n.data.properties)
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ');
      bomMap.set(key, {
        qty: 1,
        label: n.data.label,
        category: n.data.category,
        partNumber: n.data.partNumber ?? '',
        manufacturer: n.data.manufacturer ?? '',
        specs,
      });
    }
  });
  Array.from(bomMap.values())
    .sort((a, b) => a.label.localeCompare(b.label))
    .forEach((row) => bom.addRow(row));
  for (let r = 2; r <= bom.rowCount; r += 1) {
    styleDataRow(bom.getRow(r), r % 2 === 0);
  }
  bom.autoFilter = { from: 'A1', to: 'F1' };

  // ── BOM by Subsystem ──────────────────────────────────────────────────
  // Per-enclosure shopping list: each subsystem (Robot, Control Box…) with
  // its components aggregated by part + quantity, plus an order-status flag so
  // it's obvious what still needs a part number before you can order it.
  // Subsystem is parsed from the owning sheet's name ("Robot — Schematic").
  if (allSheets && allSheets.length > 0) {
    const parseSub = (name: string): string => {
      const m =
        name.match(/^(.+?)\s*[—–]\s*(.+)$/) ||
        name.match(/^(.+?)\s+-\s*(.+)$/) ||
        name.match(/^(.+?)\s*-\s+(.+)$/);
      return m ? m[1].trim() : '';
    };

    type SubBomRow = {
      qty: number;
      label: string;
      category: string;
      partNumber: string;
      manufacturer: string;
      specs: string;
    };
    // subsystem -> (part key -> row)
    const bySub = new Map<string, Map<string, SubBomRow>>();
    for (const sh of allSheets) {
      const sub = parseSub(sh.name) || '(Unassigned)';
      for (const node of sh.nodes) {
        if (!isCompNode(node)) continue;
        const map = bySub.get(sub) ?? bySub.set(sub, new Map()).get(sub)!;
        const key = [
          (node.data.partNumber ?? '').trim().toLowerCase(),
          (node.data.manufacturer ?? '').trim().toLowerCase(),
          node.data.label.trim().toLowerCase(),
        ].join('::');
        const existing = map.get(key);
        if (existing) {
          existing.qty += 1;
        } else {
          map.set(key, {
            qty: 1,
            label: node.data.label,
            category: node.data.category,
            partNumber: node.data.partNumber ?? '',
            manufacturer: node.data.manufacturer ?? '',
            specs: Object.entries(node.data.properties)
              .map(([k, v]) => `${k}: ${v}`)
              .join('; '),
          });
        }
      }
    }

    const subBom = wb.addWorksheet('BOM by Subsystem', {
      views: [{ state: 'frozen', ySplit: 1 }],
    });
    subBom.columns = [
      { header: 'Subsystem', key: 'subsystem', width: 18 },
      { header: 'Qty', key: 'qty', width: 6 },
      { header: 'Label', key: 'label', width: 26 },
      { header: 'Category', key: 'category', width: 14 },
      { header: 'Part Number', key: 'partNumber', width: 18 },
      { header: 'Manufacturer', key: 'manufacturer', width: 18 },
      { header: 'Order status', key: 'status', width: 18 },
      { header: 'Specs', key: 'specs', width: 50 },
    ];
    styleHeader(subBom.getRow(1));

    const subNames = Array.from(bySub.keys()).sort((a, b) => {
      if (a === '(Unassigned)') return 1;
      if (b === '(Unassigned)') return -1;
      return a.localeCompare(b);
    });
    for (const sub of subNames) {
      const rows = Array.from(bySub.get(sub)!.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      let subQty = 0;
      let missing = 0;
      for (const r of rows) {
        subQty += r.qty;
        const noPart = !r.partNumber.trim();
        if (noPart) missing += 1;
        const added = subBom.addRow({
          subsystem: sub,
          qty: r.qty,
          label: r.label,
          category: r.category,
          partNumber: r.partNumber,
          manufacturer: r.manufacturer,
          status: noPart ? '⚠ ADD PART No.' : 'ready to order',
          specs: r.specs,
        });
        styleDataRow(added, added.number % 2 === 0);
        if (noPart) {
          const cell = added.getCell('status');
          cell.font = { bold: true, color: { argb: 'FF9A3412' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4D5' } };
        }
      }
      // subsystem subtotal row
      const total = subBom.addRow({
        subsystem: `${sub} — total`,
        qty: subQty,
        label: `${rows.length} part${rows.length === 1 ? '' : 's'}`,
        status: missing > 0 ? `${missing} need part no.` : 'complete',
      });
      total.font = { bold: true };
      total.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      });
    }
    subBom.autoFilter = { from: 'A1', to: 'H1' };
  }

  // Pins sheet
  const pins = wb.addWorksheet('Pins', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  pins.columns = [
    { header: 'Component', key: 'component', width: 28 },
    { header: 'Connector', key: 'group', width: 20 },
    { header: 'Pin', key: 'pin', width: 16 },
    { header: 'Side', key: 'side', width: 8 },
    { header: 'Kind', key: 'kind', width: 10 },
    { header: 'Voltage', key: 'voltage', width: 12 },
    { header: 'Notes', key: 'notes', width: 40 },
  ];
  styleHeader(pins.getRow(1));
  nodes.forEach((n) => {
    n.data.pins.forEach((p) => {
      pins.addRow({
        component: n.data.label,
        group: p.group ?? '',
        pin: p.name,
        side: p.side,
        kind: p.kind,
        voltage: p.voltage ?? '',
        notes: p.notes ?? '',
      });
    });
  });
  for (let r = 2; r <= pins.rowCount; r += 1) {
    styleDataRow(pins.getRow(r), r % 2 === 0);
  }
  pins.autoFilter = { from: 'A1', to: 'G1' };

  // Wiring sheet
  const specBundleById = new Map(
    (allSheets ?? [])
      .filter((sh) => sh.kind === 'interconnect')
      .flatMap((sh) => sh.bundles ?? [])
      .map((b) => [b.id, b]),
  );
  const wiring = wb.addWorksheet('Wiring', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  wiring.columns = [
    { header: '#', key: 'idx', width: 5 },
    { header: 'Wire No', key: 'wireNo', width: 10 },
    { header: 'Harness', key: 'harness', width: 18 },
    { header: 'From Component', key: 'fromComp', width: 28 },
    { header: 'From Connector', key: 'fromConn', width: 20 },
    { header: 'From Pin', key: 'fromPin', width: 16 },
    { header: 'To Component', key: 'toComp', width: 28 },
    { header: 'To Connector', key: 'toConn', width: 20 },
    { header: 'To Pin', key: 'toPin', width: 16 },
    { header: 'Net', key: 'netName', width: 16 },
    { header: 'Label', key: 'label', width: 18 },
    { header: 'Gauge (AWG)', key: 'gauge', width: 12 },
    { header: 'Color', key: 'color', width: 14 },
    { header: 'Color 2', key: 'color2', width: 12 },
    { header: 'Notes', key: 'notes', width: 32 },
  ];
  styleHeader(wiring.getRow(1));
  edges.forEach((e, i) => {
    const src = nodes.find((n) => n.id === e.source);
    const tgt = nodes.find((n) => n.id === e.target);
    const srcPin = src?.data.pins.find((p) => p.id === e.sourceHandle);
    const tgtPin = tgt?.data.pins.find((p) => p.id === e.targetHandle);
    wiring.addRow({
      idx: i + 1,
      wireNo: e.data?.wireNo ?? '',
      harness: e.data?.harnessId ? specBundleById.get(e.data.harnessId)?.name ?? '' : '',
      fromComp: src?.data.label ?? '?',
      fromConn: srcPin?.group ?? '',
      fromPin: srcPin?.name ?? '?',
      toComp: tgt?.data.label ?? '?',
      toConn: tgtPin?.group ?? '',
      toPin: tgtPin?.name ?? '?',
      netName: (e.data?.netName ?? '').trim() || (e.data?.label ?? ''),
      label: e.data?.label ?? '',
      gauge: e.data?.gauge ?? '',
      color: e.data?.color ?? '',
      color2: e.data?.color2 ?? '',
      notes: e.data?.notes ?? '',
    });
  });
  for (let r = 2; r <= wiring.rowCount; r += 1) {
    styleDataRow(wiring.getRow(r), r % 2 === 0);
  }
  wiring.autoFilter = { from: 'A1', to: 'O1' };

  // Nets sheet — derived electrical connectivity.
  const nets = computeNets(allSheets ?? []);
  if (nets.length > 0) {
    const ns = wb.addWorksheet('Nets', { views: [{ state: 'frozen', ySplit: 1 }] });
    ns.columns = [
      { header: 'Net', key: 'net', width: 22 },
      { header: 'Type', key: 'type', width: 10 },
      { header: 'Component', key: 'comp', width: 28 },
      { header: 'Pin', key: 'pin', width: 16 },
      { header: 'Sheet', key: 'sheet', width: 22 },
    ];
    styleHeader(ns.getRow(1));
    for (const net of nets) {
      if (net.pins.length === 0) {
        ns.addRow({ net: net.name, type: net.source, comp: '', pin: '', sheet: '' });
      } else {
        for (const p of net.pins) {
          ns.addRow({ net: net.name, type: net.source, comp: p.component, pin: p.pinName, sheet: p.sheet });
        }
      }
    }
    for (let r = 2; r <= ns.rowCount; r += 1) styleDataRow(ns.getRow(r), r % 2 === 0);
    ns.autoFilter = { from: 'A1', to: 'E1' };
  }

  await saveWorkbook(wb, filename);
}
